import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2, XCircle, AlertTriangle, Landmark, Percent, TrendingUp, Users, Send, RefreshCw, Radio, Download,
} from "lucide-react";
import { euro, dateTime } from "@/lib/format";
import { toast } from "sonner";

const SOURCE_LABELS = {
  photo_wallet: "Achat photo (portefeuille)",
  photo_card: "Achat photo (carte)",
  creator_subscription: "Abonnement créateur",
};

const ModeBadge = ({ live }) => (
  <Badge
    data-testid="stripe-mode-badge"
    className={live
      ? "bg-[hsl(160_84%_32%)] text-white border-0 gap-1"
      : "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0 gap-1"}
  >
    <Radio className="h-3 w-3" /> {live ? "LIVE — argent réel" : "TEST — aucun argent réel"}
  </Badge>
);

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <Card className="p-4 rounded-2xl" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4 text-primary" /> {label}</div>
    <p className="font-display text-2xl">{value}</p>
    {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
  </Card>
);

const currentMonth = () => new Date().toISOString().slice(0, 7);

export const RevenuePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentMonth());
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/export.csv?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mihodpay-comptabilite-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${month} téléchargé`);
    } catch (e) {
      toast.error(apiError(e, "Export impossible"));
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/revenue?days=30");
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  if (!data) return null;
  const t = data.totals;
  const maxFee = Math.max(0.01, ...data.series.map((s) => s.platform_fee));

  return (
    <div className="space-y-4" data-testid="admin-revenue-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Commission plateforme <strong>{data.fee_percent}%</strong> sur les photos · abonnements <strong>100 %</strong> plateforme.</p>
        <ModeBadge live={data.stripe_mode === "live"} />
      </div>

      <Card className="p-3 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-2" data-testid="revenue-export">
        <div className="flex items-center gap-2 text-sm flex-1"><Download className="h-4 w-4 text-primary" /> Export comptable mensuel (CSV : ventes, commissions, paiements, remboursements, virements)</div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl sm:w-44" data-testid="export-month-input" />
        <Button className="rounded-xl" onClick={exportCsv} disabled={exporting || !month} data-testid="export-csv-button">
          <Download className="h-4 w-4 mr-1" /> {exporting ? "Export…" : "Télécharger"}
        </Button>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Percent} label="Mes revenus (commission)" value={euro(t.platform_revenue)} hint={`${t.sales_count} ventes`} testId="revenue-platform" />
        <StatCard icon={TrendingUp} label="Ventes brutes" value={euro(t.gross_sales)} testId="revenue-gross" />
        <StatCard icon={Users} label="Part créateurs (80%)" value={euro(t.creator_earnings)} hint={`Versé via Connect : ${euro(t.creators_paid_out)}`} testId="revenue-creators" />
        <StatCard icon={Landmark} label="Déjà viré sur ma banque" value={euro(t.platform_paid_out)} testId="revenue-paid-out" />
      </div>

      <Card className="p-4 rounded-2xl" data-testid="revenue-chart">
        <p className="text-xs font-medium mb-3">Commission encaissée — 30 derniers jours</p>
        <div className="flex items-end gap-[3px] h-24">
          {data.series.map((s) => (
            <div key={s.date} className="flex-1 h-full flex flex-col justify-end" title={`${s.date} : ${euro(s.platform_fee)} (${s.count} ventes)`}>
              <div className="w-full rounded-t bg-primary/80 min-h-[2px]" style={{ height: `${Math.max(2, (s.platform_fee / maxFee) * 100)}%` }} />
            </div>
          ))}
        </div>
      </Card>

      {Object.keys(data.by_source).length > 0 && (
        <Card className="rounded-2xl overflow-hidden" data-testid="revenue-by-source">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Ventes</TableHead>
                <TableHead className="text-right">Brut</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Créateurs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(data.by_source).map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell className="font-medium">{SOURCE_LABELS[k] || k}</TableCell>
                  <TableCell className="text-right">{v.count}</TableCell>
                  <TableCell className="text-right">{euro(v.gross)}</TableCell>
                  <TableCell className="text-right font-semibold text-primary">{euro(v.platform_fee)}</TableCell>
                  <TableCell className="text-right">{euro(v.creator_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="rounded-2xl overflow-hidden" data-testid="revenue-recent">
        <div className="px-4 py-3 border-b border-border text-xs font-medium">Dernières ventes (ledger commission)</div>
        {data.recent.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">Aucune vente enregistrée pour le moment.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Vendeur → Acheteur</TableHead>
                <TableHead className="text-right">Brut</TableHead>
                <TableHead className="text-right">Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent.slice(0, 30).map((r) => (
                <TableRow key={r.id} data-testid={`revenue-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dateTime(r.created_at)}</TableCell>
                  <TableCell className="text-xs">{SOURCE_LABELS[r.source] || r.source}</TableCell>
                  <TableCell className="text-xs">@{r.seller_username || "?"} → @{r.buyer_username || "?"}</TableCell>
                  <TableCell className="text-right text-xs">{euro(r.gross_amount)}</TableCell>
                  <TableCell className="text-right text-xs font-semibold text-primary">{euro(r.platform_fee)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export const PayoutPanel = () => {
  const [bal, setBal] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/admin/stripe/balance"), api.get("/admin/stripe/status")]);
      setBal(b.data);
      setStatus(s.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doPayout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stripe/payout", { amount: Number(amount) });
      toast.success(data.message);
      setAmount("");
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  if (!bal) return (
    <div className="text-center py-12 text-muted-foreground">
      <p>Impossible de lire le solde Stripe.</p>
      <Button variant="outline" className="mt-3 rounded-xl" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Réessayer</Button>
    </div>
  );

  const bank = status?.bank_account;
  const canPayout = Number(amount) >= 1 && Number(amount) <= bal.available && !!bank;

  return (
    <div className="space-y-4" data-testid="admin-payout-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Virements de vos revenus depuis Stripe vers votre compte bancaire (Revolut).</p>
        <ModeBadge live={bal.stripe_mode === "live"} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Landmark} label="Solde Stripe disponible" value={euro(bal.available)} hint="Peut être viré maintenant" testId="stripe-available" />
        <StatCard icon={TrendingUp} label="En cours de traitement" value={euro(bal.pending)} hint="Disponible sous 2 à 7 jours" testId="stripe-pending" />
      </div>

      <Card className="p-4 rounded-2xl space-y-3" data-testid="payout-form">
        <div className="flex items-start gap-2 text-xs">
          {bank ? (
            <p className="text-[hsl(160_84%_38%)] flex items-center gap-1.5" data-testid="payout-bank-ok">
              <CheckCircle2 className="h-4 w-4" /> Destination : {bank.bank_name || "Banque"} ••••{bank.last4} ({(bank.currency || "").toUpperCase()})
            </p>
          ) : (
            <p className="text-[hsl(38_92%_38%)] flex items-start gap-1.5" data-testid="payout-bank-missing">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Aucun compte bancaire dans Stripe. Ajoutez votre IBAN Revolut : Dashboard Stripe → Paramètres → Virements.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Input type="number" min={1} step="0.01" placeholder="Montant en €" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" data-testid="payout-amount-input" />
          <Button variant="outline" className="rounded-xl" onClick={() => setAmount(String(bal.available))} disabled={bal.available < 1} data-testid="payout-max">Tout</Button>
          <Button className="rounded-xl" disabled={!canPayout || busy} onClick={() => setConfirm(true)} data-testid="payout-submit">
            <Send className="h-4 w-4 mr-1" /> Virer
          </Button>
        </div>
        {status?.account?.payout_schedule && (
          <p className="text-[11px] text-muted-foreground">
            Virements automatiques Stripe : {status.account.payout_schedule.interval === "manual" ? "désactivés (manuel)" : `${status.account.payout_schedule.interval}, délai ${status.account.payout_schedule.delay_days} j`}.
          </p>
        )}
      </Card>

      <Card className="rounded-2xl overflow-hidden" data-testid="payout-history">
        <div className="px-4 py-3 border-b border-border text-xs font-medium">Historique des virements Stripe</div>
        {bal.payouts.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">Aucun virement pour le moment.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Montant</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bal.payouts.map((p) => (
                <TableRow key={p.id} data-testid={`payout-row-${p.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dateTime(new Date(p.created * 1000).toISOString())}</TableCell>
                  <TableCell className="text-xs">{p.automatic ? "Automatique" : "Manuel"}</TableCell>
                  <TableCell>
                    <Badge className={p.status === "paid" ? "bg-[hsl(160_84%_32%)] text-white border-0" : p.status === "failed" ? "bg-destructive/15 text-destructive border-0" : "bg-secondary text-muted-foreground border-0"}>
                      {p.status}
                    </Badge>
                    {p.failure_message && <p className="text-[10px] text-destructive mt-0.5">{p.failure_message}</p>}
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold">{euro(p.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le virement</AlertDialogTitle>
            <AlertDialogDescription>
              {euro(Number(amount))} seront virés depuis votre solde Stripe vers {bank?.bank_name || "votre banque"} ••••{bank?.last4}. Cette opération est réelle et irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="payout-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={doPayout} disabled={busy} data-testid="payout-confirm">Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const ProductionPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const setupWebhook = async () => {
    setBusy("webhook");
    try {
      const { data } = await api.post("/admin/stripe/setup-webhook", { origin_url: window.location.origin });
      toast.success(`${data.message} — ${data.url}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const setupCatalog = async () => {
    setBusy("catalog");
    try {
      const { data } = await api.post("/admin/stripe/setup-catalog");
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/status");
      setStatus(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>;
  if (!status) return null;

  const errors = status.checks.filter((c) => !c.ok && c.level === "error").length;

  return (
    <div className="space-y-4" data-testid="admin-production-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Checklist de passage en production (Stripe Live).</p>
          {status.account && (
            <p className="text-[11px] text-muted-foreground mt-0.5">Compte {status.account.id} · {status.account.country} · {status.account.business_name || "—"}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ModeBadge live={status.live} />
          <Button variant="outline" size="sm" className="rounded-xl" onClick={load} data-testid="production-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card className={`p-4 rounded-2xl border ${status.production_ready ? "border-[hsl(160_84%_32%)]/40" : "border-[hsl(38_92%_50%)]/40"}`} data-testid="production-summary">
        <div className="flex items-center gap-2">
          {status.production_ready ? <CheckCircle2 className="h-5 w-5 text-[hsl(160_84%_38%)]" /> : <AlertTriangle className="h-5 w-5 text-[hsl(38_92%_45%)]" />}
          <p className="font-medium">
            {status.production_ready ? "Prêt pour les paiements réels" : `${errors} point${errors > 1 ? "s" : ""} à corriger avant les paiements réels`}
          </p>
        </div>
        {status.test_login_enabled && (
          <p className="text-xs text-[hsl(38_92%_38%)] mt-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Le login de test est actif (désactivé automatiquement en mode live).</p>
        )}
      </Card>

      <Card className="rounded-2xl divide-y divide-border overflow-hidden" data-testid="production-checks">
        {status.checks.map((c, i) => (
          <div key={`${c.key}-${i}`} className="flex items-start gap-3 px-4 py-3" data-testid={`check-${c.key}`}>
            {c.ok
              ? <CheckCircle2 className="h-5 w-5 text-[hsl(160_84%_38%)] shrink-0 mt-0.5" />
              : c.level === "error"
                ? <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                : <AlertTriangle className="h-5 w-5 text-[hsl(38_92%_45%)] shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <p className="text-sm font-medium">{c.label}</p>
              {c.detail && <p className="text-xs text-muted-foreground break-words">{c.detail}</p>}
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-4 rounded-2xl space-y-3" data-testid="production-webhook-info">
        <div>
          <p className="text-xs font-medium">Webhook Stripe ({status.live ? "LIVE" : "TEST"})</p>
          <code className="block text-xs bg-secondary rounded-lg px-3 py-2 break-all mt-1" data-testid="webhook-url">{status.webhook_url || `${window.location.origin}${status.webhook_url_hint}`}</code>
          {status.webhook_endpoint_id && <p className="text-[11px] text-muted-foreground mt-1">Endpoint : {status.webhook_endpoint_id}</p>}
          <p className="text-[11px] text-muted-foreground mt-1">Événements : {status.webhook_events.join(", ")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" className="rounded-xl flex-1" onClick={setupWebhook} disabled={!!busy} data-testid="setup-webhook-button">
            <Send className="h-4 w-4 mr-1" /> {busy === "webhook" ? "Création…" : "Créer le webhook automatiquement"}
          </Button>
          <Button variant="outline" className="rounded-xl flex-1" onClick={setupCatalog} disabled={!!busy} data-testid="setup-catalog-button">
            <RefreshCw className="h-4 w-4 mr-1" /> {busy === "catalog" ? "Création…" : "Créer le catalogue de prix"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Le secret de signature (whsec_) est conservé côté serveur uniquement — jamais transmis au navigateur.</p>
      </Card>
    </div>
  );
};
